import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import React, { useEffect, useState } from "react"
import { CheckCircleOutline } from "@mui/icons-material"
import { toast } from "react-toastify"
import { getFileSizeFromBytes, Icons } from "../../../utils/utilities"
import VisuallyHiddenInput from "../../common/VisuallyHiddenInput"
import CustomChip from "../../common/CustomChip"
import Progress from "../../common/Progress"
import api from "../../../service/api"
const MANUAL_ROLES = [
    {
        value: "STUDENT",
        label: "Student",
    },
    {
        value: "FACULTY",
        label: "Faculty",
    },
    {
        value: "HOD",
        label: "HOD",
    },
    {
        value: "PRINCIPAL",
        label: "Principal",
    },
]
const EMPTY_FORM = {
    audiences: ["FACULTY"],
    module: "",
    title: "",
    description: "",
    display_order: 1,
}
const DEFAULT_UPLOAD_STATUS = {
    progress: 0,
    status: "Queued",
    statusId: 1,
}
const AddManualDialog = ( { open, setOpen, manual = null, refresh } ) => {
    const [form, setForm] = useState( EMPTY_FORM )
    const [selectedFile, setSelectedFile] = useState( null )
    const [uploading, setUploading] = useState( false )
    const [uploadStatus, setUploadStatus] = useState( DEFAULT_UPLOAD_STATUS )
    const { palette, border, breakpoints } = useTheme()
    const xs = useMediaQuery( breakpoints.down( "sm" ) )
    const isEditMode = Boolean( manual )
    // LOAD FORM
    useEffect( () => {
        if ( !open ) {
            return
        }
        setForm(
            manual
                ? {
                    audiences:
                        Array.isArray( manual.audiences ) && manual.audiences.length > 0
                            ? manual.audiences
                            : ["FACULTY"],
                    module: manual.module || "",
                    title: manual.title || "",
                    description: manual.description || "",
                    display_order: manual.display_order ?? 1,
                }
                : {
                    ...EMPTY_FORM,
                },
        )
        setSelectedFile( null )
        setUploadStatus( DEFAULT_UPLOAD_STATUS )
    }, [open, manual] )
    // STATUS
    const updateStatus = ( progress, status, statusId ) => {
        setUploadStatus( {
            progress,
            status,
            statusId,
        } )
    }
    // FIELD CHANGE
    const handleChange = ( event ) => {
        const { name, value } = event.target
        setForm( ( previous ) => ( {
            ...previous,
            [name]: value,
        } ) )
    }
    // ROLE TOGGLE
    const toggleRole = ( role ) => {
        if ( uploading ) {
            return
        }
        setForm( ( previous ) => {
            const selected = previous.audiences.includes( role )
            return {
                ...previous,
                audiences: selected
                    ? previous.audiences.filter( ( value ) => value !== role )
                    : [...previous.audiences, role],
            }
        } )
    }
    // SELECT PDF
    const handleFileSelection = ( event ) => {
        const file = event.target.files?.[0]
        event.target.value = ""
        if ( !file ) {
            return
        }
        const extension = file.name?.split( "." )?.pop()?.toLowerCase()
        if ( extension !== "pdf" ) {
            toast( "Only PDF manual files are supported." )
            return
        }
        if (
            file.type &&
            !["application/pdf", "application/octet-stream"].includes( file.type )
        ) {
            toast( "Invalid PDF file." )
            return
        }
        // Keep frontend limit aligned with backend default.
        const maxSizeMB = 50
        if ( file.size > maxSizeMB * 1024 * 1024 ) {
            toast( `Manual PDF cannot exceed ${maxSizeMB}MB.` )
            return
        }
        setSelectedFile( file )
        // Auto-fill title when empty.
        if ( !form.title?.trim() ) {
            const title = file.name.replace( /\.pdf$/i, "" )
            setForm( ( previous ) => ( {
                ...previous,
                title,
            } ) )
        }
    }
    // RESET / CLOSE
    const resetDialog = () => {
        setSelectedFile( null )
        setUploadStatus( DEFAULT_UPLOAD_STATUS )
    }
    const closeDialog = () => {
        if ( uploading ) {
            return
        }
        resetDialog()
        setOpen( false )
    }
    // VALIDATION
    const validateForm = () => {
        if ( !Array.isArray( form.audiences ) || form.audiences.length === 0 ) {
            toast( "Please select at least one role." )
            return false
        }
        const invalidRole = form.audiences.find(
            ( role ) => !MANUAL_ROLES.some( ( item ) => item.value === role ),
        )
        if ( invalidRole ) {
            toast( "Please select valid roles." )
            return false
        }
        if ( !form.module?.trim() ) {
            toast( "Please enter a module name." )
            return false
        }
        if ( !form.title?.trim() ) {
            toast( "Please enter a manual title." )
            return false
        }
        const displayOrder = Number( form.display_order )
        if ( Number.isNaN( displayOrder ) || displayOrder < 0 ) {
            toast( "Display order must be a valid non-negative number." )
            return false
        }
        if ( !isEditMode && !selectedFile ) {
            toast( "Please select a PDF file." )
            return false
        }
        return true
    }
    // BUILD FORM DATA
    // IMPORTANT: Backend multer expects file.
    const buildFormData = () => {
        const formData = new FormData()
        if ( selectedFile ) {
            formData.append( "file", selectedFile, selectedFile.name )
        }
        formData.append( "audiences", JSON.stringify( form.audiences ) )
        formData.append( "module", form.module.trim() )
        formData.append( "title", form.title.trim() )
        formData.append( "description", form.description?.trim() || "" )
        formData.append( "display_order", String( Number( form.display_order ) || 0 ) )
        return formData
    }
    // SAVE
    const saveManual = async () => {
        if ( !validateForm() ) {
            return
        }
        try {
            setUploading( true )
            updateStatus(
                0,
                isEditMode ? "Updating manual..." : "Uploading manual...",
                3,
            )
            const formData = buildFormData()
            const progressHandlers = {
                onUploadProgress: ( event ) => {
                    if ( !event.total ) {
                        return
                    }
                    const progress = Math.round( ( event.loaded * 100 ) / event.total )
                    updateStatus( progress, "Uploading...", 3 )
                },
            }
            if ( isEditMode ) {
                await api.tutorialsAndManuals.updateManual( manual.id, formData, progressHandlers )
            } else {
                await api.tutorialsAndManuals.addManual( formData, progressHandlers )
            }
            updateStatus( 100, "Completed", 5 )
            toast.success(
                isEditMode
                    ? "Manual updated successfully."
                    : "Manual uploaded successfully.",
            )
            if ( typeof refresh === "function" ) {
                await refresh()
            }
            resetDialog()
            setOpen( false )
        } catch ( err ) {
            console.log( "Manual upload error:", err )
            console.log( "Manual upload response:", err?.response )
            updateStatus( 0, "Failed", -1 )
            if ( err?.response?.status === 413 ) {
                toast( "Manual PDF is too large." )
                return
            }
            toast(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to save the manual.",
            )
        } finally {
            setUploading( false )
        }
    }
    return (
        <Dialog
            disableRestoreFocus
            fullScreen={xs}
            open={open}
            onClose={closeDialog}
            slotProps={{
                paper: {
                    sx: {
                        width: {
                            xs: "100%",
                            sm: "90%",
                        },
                        maxWidth: "700px",
                        maxHeight: "90vh",
                    },
                },
            }}
        >
            {/* HEADER */}

            <DialogTitle
                sx={{
                    borderBottom: border[1],
                    zIndex: 5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "20px",
                    padding: "10px 20px",
                }}
            >
                <Typography
                    variant="h1"
                    fontWeight="600"
                    component="span"
                    fontSize="16px"
                >
                    {isEditMode
                        ? `Edit manual: ${manual?.title || ""}`
                        : "Add new manual"}
                </Typography>

                <IconButton onClick={closeDialog} disabled={uploading}>
                    {Icons.default.CloseIcon}
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: 0,
                }}
            >
                <Box padding="20px">
                    {/* TITLE */}

                    <FormControl margin="normal" fullWidth>
                        <Typography
                            gutterBottom
                            variant="subtitle2"
                            color={palette.form.labelColor}
                        >
                            What is this manual about? *
                        </Typography>

                        <TextField
                            size="small"
                            name="title"
                            value={form.title}
                            disabled={uploading}
                            onChange={handleChange}
                            placeholder="Eg: Assignment User Manual"
                        />
                    </FormControl>

                    {/* DESCRIPTION */}

                    <FormControl margin="normal" fullWidth>
                        <Typography
                            gutterBottom
                            variant="subtitle2"
                            color={palette.form.labelColor}
                        >
                            Explain in details here...
                        </Typography>

                        <TextField
                            size="small"
                            multiline
                            minRows={4}
                            name="description"
                            value={form.description}
                            disabled={uploading}
                            onChange={handleChange}
                            placeholder="Explain what this manual contains..."
                        />
                    </FormControl>

                    {/* MODULE */}

                    <FormControl margin="normal" fullWidth>
                        <Typography
                            gutterBottom
                            variant="subtitle2"
                            color={palette.form.labelColor}
                        >
                            Select the module for this manual... *
                        </Typography>

                        <TextField
                            size="small"
                            name="module"
                            value={form.module}
                            disabled={uploading}
                            onChange={handleChange}
                            placeholder="Eg: Assignments"
                        />
                    </FormControl>

                    {/* ROLES */}

                    <FormControl margin="normal" fullWidth>
                        <Typography
                            gutterBottom
                            variant="subtitle2"
                            color={palette.form.labelColor}
                        >
                            Select the user group that can view this manual... *
                        </Typography>

                        <Box display="flex" flexWrap="wrap" gap="10px">
                            {MANUAL_ROLES.map( ( role ) => {
                                const selected = form.audiences.includes( role.value )
                                return (
                                    <Box
                                        key={role.value}
                                        borderRadius="30px"
                                        border={`2px solid ${palette.borderColor}`}
                                        display="flex"
                                        alignItems="center"
                                        gap="10px"
                                        padding="5px 15px"
                                        onClick={() => toggleRole( role.value )}
                                        bgcolor={
                                            selected ? palette.success.main : palette.greyedOut
                                        }
                                        sx={{
                                            transition: "300ms",
                                            cursor: uploading ? "not-allowed" : "pointer",
                                            opacity: uploading ? 0.6 : 1,
                                            borderColor: selected
                                                ? palette.success.main
                                                : palette.borderColor,
                                            "&:hover": {
                                                borderColor: selected
                                                    ? palette.success.main
                                                    : palette.common.font,
                                            },
                                        }}
                                    >
                                        {selected && (
                                            <CheckCircleOutline
                                                sx={{
                                                    color: palette.common.white,
                                                }}
                                                fontSize="small"
                                            />
                                        )}

                                        <Typography
                                            color={
                                                selected ? palette.common.white : palette.common.font
                                            }
                                            fontWeight="500"
                                            fontSize="14px"
                                        >
                                            {role.label}
                                        </Typography>
                                    </Box>
                                )
                            } )}
                        </Box>
                    </FormControl>

                    {/* PDF */}

                    <FormControl margin="normal" fullWidth>
                        <Typography
                            gutterBottom
                            variant="subtitle2"
                            color={palette.form.labelColor}
                        >
                            Manual PDF *
                        </Typography>

                        <Box
                            borderRadius="5px"
                            border={border[1]}
                            bgcolor={palette.form.inputBg}
                            padding="12px"
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                gap="10px"
                                flexWrap="wrap"
                            >
                                <Button
                                    component="label"
                                    tabIndex={-1}
                                    size="small"
                                    color="greyed"
                                    disabled={uploading}
                                    startIcon={Icons.default.CloudUpload}
                                    sx={{
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {selectedFile
                                        ? "Change file"
                                        : isEditMode
                                            ? "Replace file"
                                            : "Select PDF"}

                                    <VisuallyHiddenInput
                                        type="file"
                                        accept="application/pdf,.pdf"
                                        onChange={handleFileSelection}
                                    />
                                </Button>

                                {selectedFile && (
                                    <Typography fontSize="12px" color="textSecondary">
                                        {selectedFile.name}
                                    </Typography>
                                )}
                            </Box>

                            {selectedFile && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap="10px"
                                    marginTop="12px"
                                >
                                    <CustomChip value={`PDF: ${selectedFile.name}`} />

                                    <CustomChip
                                        startIcon={Icons.default.Storage}
                                        value={`Size: ${getFileSizeFromBytes( selectedFile.size )}`}
                                    />
                                </Box>
                            )}

                            {isEditMode && !selectedFile && manual?.file_details && (
                                <Box display="flex" flexWrap="wrap" gap="10px" marginTop="12px">
                                    {manual?.file_details?.name && (
                                        <CustomChip value={manual.file_details.name} />
                                    )}

                                    {manual?.file_details?.size && (
                                        <CustomChip
                                            startIcon={Icons.default.Storage}
                                            value={`Size: ${getFileSizeFromBytes(
                                                manual.file_details.size,
                                            )}`}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </FormControl>

                    {/* DISPLAY ORDER */}

                    <FormControl
                        margin="normal"
                        sx={{
                            maxWidth: "250px",
                        }}
                    >
                        <Typography
                            gutterBottom
                            variant="subtitle2"
                            color={palette.form.labelColor}
                        >
                            Display Order
                        </Typography>

                        <TextField
                            size="small"
                            name="display_order"
                            type="number"
                            value={form.display_order}
                            disabled={uploading}
                            onChange={handleChange}
                            inputProps={{
                                min: 0,
                            }}
                        />
                    </FormControl>

                    {/* PROGRESS */}

                    {[2, 3, 4, 5].includes( uploadStatus.statusId ) && (
                        <Box display="flex" alignItems="center" gap="10px" marginTop="15px">
                            <Progress
                                size={20}
                                trackWidth={4}
                                indicatorWidth={4}
                                showProgress={false}
                                progress={uploadStatus.progress}
                            />

                            <Typography fontSize="12px" component="em" color="textSecondary">
                                {uploadStatus.status} {uploadStatus.progress}%
                            </Typography>
                        </Box>
                    )}

                    {/* ACTIONS */}

                    <Box display="flex" flexWrap="wrap" gap="20px" marginTop="40px">
                        <Button
                            disableElevation
                            disabled={uploading}
                            onClick={saveManual}
                            startIcon={
                                uploading ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    Icons.default.CloudUpload
                                )
                            }
                            variant="contained"
                            sx={{
                                textTransform: "capitalize",
                            }}
                        >
                            {uploading
                                ? isEditMode
                                    ? "Saving changes..."
                                    : "Uploading file..."
                                : isEditMode
                                    ? "Save changes"
                                    : "Add File"}
                        </Button>

                        <Button
                            disableElevation
                            color="error"
                            disabled={uploading}
                            onClick={closeDialog}
                            variant="contained"
                            sx={{
                                textTransform: "capitalize",
                            }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
export default AddManualDialog
